import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ConfigProvider } from 'antd';
import { ColumnGroupType, ColumnsType, ColumnType } from 'antd/lib/table';
import { IPOButton, IPOMessage, IPOAccount, IPOUtils } from '@hylaa-npm/ipod';

import PageContainer, { IPageRef } from '@/components/Page';
import { PSearchTool, SearchEnum } from '@/components/PSearchTool';
import Empty from '@/components/Empty';
import { FilterProjectId } from '../ProjectFn';
import { ProjectFns } from '../..';

import { getProjects, IGetProjectProps, IProjectProps } from '@/apis/project';
import { getProjectsFromClients } from '@/apis/sync';
import { clientList } from '@/apis/printer';

import { CustomTable } from '@/styles/components/CustomBlueTable';
import { ContentTitleEleWrap, ProjectWrap } from './style';

const Project: FC<{}> = () => {
	const { t } = useTranslation();
	const pageRef = useRef<IPageRef>();
	const currentUser = useSelector((state: { userInfo: IPOAccount.IStaff }) => state.userInfo);

	const [projects, setProjects] = useState<IProject[]>([]);
	const [clients, setClients] = useState<ISplitEnd[]>([]);

	const [total, setTotal] = useState<number>(0);

	const [proLoading, setProLoading] = useState<boolean>(true);

	const [search, setSearch] = useState<IProjectProps>({
		clientId: '',
		name: '',
		progressStatus: '',
	});
	const [pagination, setPagination] = useState<IPagination>({ take: 0, offset: 0 });

	useEffect(() => {
		getClientList();
		getAllProject();
	}, []);

	useEffect(() => {
		if (pageRef.current) {
			const take = pageRef.current?.pageSize;
			setPagination({ take, offset: 0 });
		}
	}, [pageRef]);

	useEffect(() => {
		getProjectList(search, pagination);
	}, [JSON.stringify(search), JSON.stringify(pagination)]);

	const ProjectColumns: ColumnsType<IProject> = [
		{
			title: '编号',
			key: 'serialNum',
			ellipsis: true,
			render: (text: unknown, info: IProject) => <span>{info.serialNum}</span>,
		},
		{
			title: t('project.projectName'),
			key: 'name',
			ellipsis: true,
			render: (text: unknown, info: IProject) => (
				<ProjectFns.Name
					id={info.id}
					name={info.name}
					client={info.client}
					isJumpRight={
						!!(currentUser.role && currentUser.role === IPOAccount.StaffRoleEnum.READ_ONLY)
					}
				/>
			),
		},
		{
			title: t('project.splitServer'),
			key: 'splitServer',
			ellipsis: true,
			render: (text: unknown, info: IProject) => <ProjectFns.Client client={info.client} />,
		},
		{
			title: t('project.startAndEndTime'),
			key: 'startEndTime',
			ellipsis: true,
			render: (text: unknown, info: IProject) => (
				<ProjectFns.Time startTime={info.startTime} endTime={info.endTime} />
			),
		},
		{
			title: t('project.state'),
			key: 'status',
			ellipsis: true,
			render: (text: unknown, info: IProject) => <ProjectFns.Status status={info.progressStatus} />,
		},

		{
			title: t('project.overview'),
			key: 'opt',
			align: 'center',
			render: (text: unknown, info: IProject) => (
				<ProjectFns.Overview projectId={info.id} client={info.client} />
			),
		},
	];

	const getProjectList: (param: IProjectProps, pagination: IPagination) => void = (
		param,
		pagination,
	) => {
		if (pagination.take > 0) {
			getProjects(param, pagination).then((res: IGetProjectProps) => {
				const data = res as { list: IProject[]; pagination: IPagination };
				let newList = FilterProjectId(data.list);
				setProjects(newList);
				setTotal(data.pagination.size ? data.pagination.size : data.list.length);
				setProLoading(false);
			});
		}
		setProLoading(false);
	};
	/**
	 * 获取所有的项目，方便监管商进入项目去查看
	 */
	const getAllProject: () => void = () => {
		getProjects({
			clientId: '',
			name: '',
			progressStatus: '',
		}).then((res: IGetProjectProps) => {
			const data = res as { list: IProject[]; pagination: IPagination };
			let newList = FilterProjectId(data.list);
			let allProject = newList.map((project) => {
				return {
					project: {
						...project,
					},
					client: project.client,
				};
			});
			let obj = {
				projects: allProject,
			};
			window.name = JSON.stringify(obj);
		});
	};
	const getClientList: () => void = () => {
		clientList().then((res: unknown) => {
			const list: ISplitEnd[] = (res as { list: ISplitEnd[] }).list;
			setClients(list);
		});
	};

	const asyncProject: () => void = () => {
		clients.forEach((item) => {
			if (item.connectState === 1) {
				getProjectsFromClients(item.id).then((res) => {
					IPOMessage.success(t('common.asyncSucces'));
				});
			}
		});
	};

	const getSearchEle: () => ReactNode = () => {
		return (
			<PSearchTool
				tools={[
					{
						key: 'name',
						placeholder: t('common.plInput'),
						type: SearchEnum.INPUT,
					},
					{
						key: 'progressStatus',
						placeholder: t('common.plSelect'),
						type: SearchEnum.SELECT,
						defaultValue: '',
						selectData: {
							seleceKey: 'key',
							selectLabel: 'name',
							selectTree: [{ name: t('common.allStates'), key: '' }, ...IPOUtils.projectStatus],
						},
					},
					{
						key: 'clientId',
						placeholder: t('common.plSelect'),
						type: SearchEnum.SELECT,
						defaultValue: '',
						selectData: {
							seleceKey: 'id',
							selectLabel: 'name',
							selectTree: [
								{ name: t('common.allClint'), id: '' },
								...((clients as unknown) as { [key: string]: string }[]),
							],
							isSearch: true,
						},
					},
				]}
				handleSearch={(value) => {
					setSearch((value as unknown) as IProjectProps);
					setPagination({ ...pagination, ...{ offset: 0 } });
				}}
			/>
		);
	};

	const getContentEle: () => ReactNode = () => {
		const getEmptyType: () => 'search' | 'nodata' = () => {
			if (search.clientId || search.name || search.progressStatus) {
				return 'search';
			} else {
				return 'nodata';
			}
		};
		return (
			<ConfigProvider
				renderEmpty={() => (
					<Empty type={getEmptyType()} tips={getEmptyType() === 'nodata' ? '暂无数据' : ''} />
				)}>
				<ProjectWrap id="project-wrap">
					<CustomTable
						rowKey="id"
						dataSource={projects}
						columns={ProjectColumns as (ColumnGroupType<object> | ColumnType<object>)[]}
						pagination={false}
						loading={proLoading}
					/>
				</ProjectWrap>
			</ConfigProvider>
		);
	};

	const getContentTitleEle: () => ReactNode = () => {
		return (
			<ContentTitleEleWrap>
				<span>{t('project.projectList')}</span>
				<IPOButton onClick={() => asyncProject()}>{t('common.async')}</IPOButton>
			</ContentTitleEleWrap>
		);
	};

	return (
		<>
			<PageContainer
				pageRef={pageRef}
				pageTitle={t('project.projectList')}
				searchEle={getSearchEle()}
				contentTitle={getContentTitleEle()}
				contentEle={getContentEle()}
				pagi={{
					total,
					offset: pagination.offset,
					handlePageChange: (offset) => setPagination({ ...pagination, ...{ offset } }),
				}}
			/>
		</>
	);
};

export default Project;
